import * as React from "react";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Money as AttachMoney,
} from "@mui/icons-material";
import PrintIcon from "@mui/icons-material/Print";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "axios";

const GetStudent = () => {
  const [searchText, setSearchText] = React.useState("");
  const { t, i18n } = useTranslation();
  const [dataSource, setDataSource] = React.useState([]);
  const [deleteItemId, setDeleteItemId] = React.useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const token = localStorage.getItem("token");

  React.useEffect(() => {
    getApiProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, i18n.language]);

  const getApiProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        "https://api.clever-school.com/api/students",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const formattedData = response.data.map((item, index) => {
        const classModelName = item.class_model?.name
          ? JSON.parse(item.class_model.name)
          : {};
        const sectionName = item.section?.name
          ? JSON.parse(item.section.name)
          : {};

        return {
          index: index + 1,
          id: item.id,
          name: item.name,
          gender: item.gender,
          phone_number: item.phone_number,
          alt_phone_number: item.alt_phone_number,
          class_model:
            classModelName[i18n.language] || item.class_model?.name || "",
          section: sectionName[i18n.language] || item.section?.name || "",
        };
      });

      setDataSource(formattedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmedDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(
        `https://api.clever-school.com/api/students/${deleteItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await getApiProducts();
      setDeleteItemId(null);
      setIsDeleteModalVisible(false);
    } catch (error) {
      console.error("Error deleting student:", error);
    } finally {
      setLoading(false);
    }
  };

  const direction = ["ar", "ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ar", "ku"].includes(i18n.language) ? "right" : "left";

  const columns = [
    { field: "index", headerName: "#", width: 20 },
    { field: "name", headerName: t("name"), width: 210 },
    { field: "gender", headerName: t("Gender"), width: 60 },
    { field: "phone_number", headerName: t("phoneNumber"), width: 110 },
    { field: "class_model", headerName: t("class"), width: 60 },
    { field: "section", headerName: t("section"), width: 60 },
    {
      field: "action",
      headerName: t("action"),
      width: 300,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Link to={`/GetStudent/UpdateStudent/${params.row.id}`}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              sx={{
                backgroundColor: "#3f51b5",
                color: "#fff",
                "&:hover": { backgroundColor: "#303f9f" },
                minWidth: "40px",
                px: 1.5,
              }}
            />
          </Link>
          <Link to={`/GetStudent/PaymentStudent/${params.row.id}`}>
            <Button
              variant="contained"
              startIcon={<AttachMoney />}
              sx={{
                backgroundColor: "#2e7d32",
                color: "#fff",
                "&:hover": { backgroundColor: "#1b5e20" },
                minWidth: "40px",
                px: 1.5,
              }}
            />
          </Link>

          <Link to={`/GetStudent/PrintStudent/${params.row.id}`}>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              sx={{
                backgroundColor: "#2196f3",
                color: "#fff",
                "&:hover": { backgroundColor: "#1976d2" },
                minWidth: "40px",
                px: 1.5,
              }}
            />
          </Link>
          <Link>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleDelete(params.row.id)}
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                "&:hover": { backgroundColor: "#b71c1c" },
                minWidth: "40px",
                px: 1.5,
              }}
            />
          </Link>
          <Link to={`/GetStudent/ViewStudent/${params.row.id}`}>
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              sx={{
                backgroundColor: "#9c27b0",
                color: "#fff",
                "&:hover": { backgroundColor: "#7b1fa2" },
                minWidth: "40px",
                px: 1.5,
              }}
            />
          </Link>
        </div>
      ),
    },
  ];

  const filteredRows = dataSource.filter((row) =>
    Object.values(row).some((val) =>
      String(val).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  return (
    <div style={{ margin: "25px", overflow: "hidden" }}>
      <Box
        sx={{
          direction,
          textAlign,
          marginBottom: "16px",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <TextField
          placeholder={t("searchHere")}
          variant="outlined"
          sx={{
            flex: "1",
            minWidth: "250px",
            maxWidth: "400px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#ddd",
              },
              "&:hover fieldset": {
                borderColor: "#999",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#3f51b5",
              },
            },
          }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Link to="/AddStudent" style={{ textDecoration: "none" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: "#3f51b5",
              color: "#fff",
              "&:hover": { backgroundColor: "#303f9f" },
              borderRadius: "6px",
              padding: "10px 20px",
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
          >
            {t("addStudent")}
          </Button>
        </Link>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "300px",
          }}
        >
          <CircularProgress size={60} sx={{ color: "#3f51b5" }} />
          <Typography variant="h6" sx={{ marginTop: 2 }}>
            {t("Loading Students...")}
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            marginTop: 2,
            marginBottom: 10,
            padding: 2,
            overflowX: "auto",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <DataGrid
            rows={filteredRows}
            columns={columns}
            autoHeight
            pagination={false}
            disableSelectionOnClick
            sx={{
              minWidth: "800px",
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#3f51b5",
                color: "black",
                "& .MuiDataGrid-columnHeaderTitle": {
                  // Add this nested selector
                  fontSize: "16px",
                  fontWeight: "bold",
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-virtualScroller": {
                minHeight: "200px !important",
              },
            }}
          />
        </Paper>
      )}

      <Dialog
        open={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
      >
        <DialogTitle>{t("confirmDelete")}</DialogTitle>
        <DialogContent>
          <p>{t("deleteConfirmation")}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteModalVisible(false)}>
            {t("cancel")}
          </Button>
          <Button onClick={handleConfirmedDelete} color="error">
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GetStudent;
